/**
 * Enumy domenowe k1p4 — klucze kanoniczne (DB/API) wg PLAN-IMPLEMENTACJI §6.1–6.2.
 * Etykiety UI NIE mieszkają tutaj — wyłącznie w @k1p4/i18n (klucze pochodne od tych wartości).
 */

// ---------------------------------------------------------------------------
// §6.1 Maszyna stanów procesu zatrudnienia — 10 etapów kanonicznych (1..10)
// ---------------------------------------------------------------------------

export const ONBOARDING_STAGES = [
  'invited',
  'email_verified',
  'data_submitted',
  'data_approved',
  'terms_prepared',
  'terms_accepted',
  'referral_issued',
  'medical_approved',
  'bhp_confirmed',
  'active',
] as const

export type OnboardingStage = (typeof ONBOARDING_STAGES)[number]

export type OnboardingStageNumber = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10

export const ONBOARDING_STAGE_NUMBER: Record<OnboardingStage, OnboardingStageNumber> = {
  invited: 1,
  email_verified: 2,
  data_submitted: 3,
  data_approved: 4,
  terms_prepared: 5,
  terms_accepted: 6,
  referral_issued: 7,
  medical_approved: 8,
  bhp_confirmed: 9,
  active: 10,
}

/** Etap kanoniczny dla numeru 1..10; poza zakresem — undefined. */
export function onboardingStageFromNumber(stageNumber: number): OnboardingStage | undefined {
  if (!Number.isInteger(stageNumber)) return undefined
  return ONBOARDING_STAGES[stageNumber - 1]
}

export type OnboardingStepState = 'done' | 'active' | 'pending'

/**
 * Stan kroku na osi procesu (Fala 6, grupa 88). Etap zapisany w aktach to
 * etap OSIĄGNIĘTY — „Dane przekazane" na etapie 3 jest zrobione, a trwa krok
 * następny. Makieta liczy tak samo (i < stage od zera); porównanie od jedynki
 * zostawiało aktywnego pracownika z krokiem 10 w stanie „Trwa".
 */
export function onboardingStepState(
  stepNumber: number,
  reachedStageNumber: number,
): OnboardingStepState {
  if (stepNumber <= reachedStageNumber) return 'done'
  if (stepNumber === reachedStageNumber + 1) return 'active'
  return 'pending'
}

// 7 faz portalu onboardingu — prezentacja pogrupowana etapów kanonicznych.

export const ONBOARDING_PHASES = [
  'joining',
  'personal_data',
  'hr_verification',
  'contract_terms',
  'medical_exams',
  'bhp_training',
  'active',
] as const

export type OnboardingPhase = (typeof ONBOARDING_PHASES)[number]

export type OnboardingPhaseNumber = 1 | 2 | 3 | 4 | 5 | 6 | 7

export const ONBOARDING_PHASE_NUMBER: Record<OnboardingPhase, OnboardingPhaseNumber> = {
  joining: 1,
  personal_data: 2,
  hr_verification: 3,
  contract_terms: 4,
  medical_exams: 5,
  bhp_training: 6,
  active: 7,
}

/** Mapowanie etap kanoniczny → faza onboardingu (§6.1: 1–2 → Dołączenie … 10 → Aktywny). */
export const ONBOARDING_STAGE_PHASE: Record<OnboardingStage, OnboardingPhase> = {
  invited: 'joining',
  email_verified: 'joining',
  data_submitted: 'personal_data',
  data_approved: 'hr_verification',
  terms_prepared: 'contract_terms',
  terms_accepted: 'contract_terms',
  referral_issued: 'medical_exams',
  medical_approved: 'medical_exams',
  bhp_confirmed: 'bhp_training',
  active: 'active',
}

export function onboardingPhaseForStage(stage: OnboardingStage): OnboardingPhase {
  return ONBOARDING_STAGE_PHASE[stage]
}

/** Stany boczne procesu (kolumna onboarding_side_state; null = przebieg nominalny). */
export const ONBOARDING_SIDE_STATES = ['question', 'rejected'] as const
export type OnboardingSideState = (typeof ONBOARDING_SIDE_STATES)[number]

// ---------------------------------------------------------------------------
// §6.2 Pozostałe maszyny stanów — dokładne klucze z planu
// ---------------------------------------------------------------------------

export const EMPLOYEE_STATUSES = ['aktywny', 'onboarding', 'terminated', 'rejected'] as const
export type EmployeeStatus = (typeof EMPLOYEE_STATUSES)[number]

export const CHANGE_REQUEST_STATUSES = ['pending', 'approved', 'rejected'] as const
export type ChangeRequestStatus = (typeof CHANGE_REQUEST_STATUSES)[number]

export const PORTAL_REQUEST_STATUSES = ['pending', 'approved', 'rejected'] as const
export type PortalRequestStatus = (typeof PORTAL_REQUEST_STATUSES)[number]

export const DOCUMENT_STATUSES = [
  'szkic',
  'do_akceptacji',
  'do_podpisu',
  'podpisany',
  'zarchiwizowany',
] as const
export type DocumentStatus = (typeof DOCUMENT_STATUSES)[number]

/** Wniosek urlopowy: change_requested = HR prosi o zmianę terminu (§6.2). */
export const LEAVE_STATUSES = ['pending', 'approved', 'rejected', 'change_requested'] as const
export type LeaveStatus = (typeof LEAVE_STATUSES)[number]

export const DOCUMENT_TYPES = [
  'umowa',
  'aneks',
  'zaswiadczenie',
  /** Świadectwo pracy — wydawane w dniu ustania zatrudnienia (art. 97 § 1 k.p.). */
  'swiadectwo',
  /**
   * Zawiadomienie o zastosowaniu kary porządkowej (art. 110 k.p.). Odpis idzie
   * do akt osobowych, a po zatarciu kary usuwa się go z nich (art. 113 § 1).
   */
  'zawiadomienie_o_karze',
  /**
   * Karta ewidencji czasu pracy — dokument z § 6 pkt 1 lit. a rozporządzenia
   * o dokumentacji pracowniczej, przechowywany osobno od akt osobowych
   * (art. 94 pkt 9a k.p.), miesiąc po miesiącu (Etap 39).
   */
  'ewidencja_czasu_pracy',
  /**
   * Oświadczenie pracodawcy po zajęciu wynagrodzenia (art. 882 § 1 k.p.c.):
   * zestawienie wynagrodzenia za trzy miesiące przed zajęciem, kwoty i terminy
   * przekazywania oraz przeszkody. Termin — tydzień od doręczenia wezwania.
   */
  'oswiadczenie_zajecie',
  /**
   * Zgłoszenie umowy o dzieło do ZUS na formularzu RUD (art. 36 ust. 17 ustawy
   * o systemie ubezpieczeń społecznych). Wydruk pomocniczy: sam formularz
   * składa człowiek na PUE ZUS albo na papierze (Etap 47, ADR-0048).
   */
  'zgloszenie_rud',
  'pit11',
  /**
   * Informacja IFT-1R o przychodzie nierezydenta i pobranym podatku u źródła
   * (art. 42 ust. 2 pkt 2 ustawy o PIT). Wydruk pomocniczy — jak PIT-11
   * i IMIR — przechowywany poza aktami osobowymi (Etap 48, ADR-0049).
   */
  'ift1r',
  /**
   * Informacja IFT-1 wystawiona na pisemny wniosek podatnika w trakcie roku
   * (art. 42 ust. 4 ustawy o PIT): ten sam wzór co IFT-1R, ale za rok DO DNIA
   * złożenia wniosku, z terminem czternastu dni (Etap 52, ADR-0053).
   */
  'ift1',
  'rmua',
  'regulamin',
  /**
   * Odpis klauzuli informacyjnej z art. 13 RODO zaakceptowanej w procesie
   * zatrudnienia — ta sama treść, której skrót SHA-256 zapisano jako dowód
   * zapoznania (Fala 5, grupa 134). U pracownika w części B akt.
   */
  'klauzula_rodo',
  /**
   * Odpis informacji o warunkach zatrudnienia z art. 29 § 3 k.p. — obowiązek
   * wyłącznie wobec pracownika, więc powstaje tylko przy umowie o pracę;
   * potwierdzenie zapoznania należy do części B akt (§ 3 rozporządzenia
   * o dokumentacji pracowniczej).
   */
  'informacja_o_warunkach',
  'orzeczenie',
  'protokol_bhp',
  'inny',
] as const
export type DocumentType = (typeof DOCUMENT_TYPES)[number]

/**
 * Dokumenty rozliczeniowe w zakładce „ZUS / PIT / PPK" portalu (spec 03 §7):
 * informacje dla ubezpieczonego i podatnika. Poza PIT-11 i ZUS IMIR także
 * IFT-1R i IFT-1 na wniosek — ADR-0049 §8 i ADR-0053 każą je pokazać „jak
 * IMIR i PIT-11", a zakładka ich nie widziała (grupy 98 i 161).
 */
export const SETTLEMENT_DOCUMENT_TYPES = [
  'pit11',
  'rmua',
  'ift1r',
  'ift1',
] as const satisfies readonly DocumentType[]

export const TIME_PERIOD_STATUSES = ['open', 'review', 'closed'] as const
export type TimePeriodStatus = (typeof TIME_PERIOD_STATUSES)[number]

// Klucz 'zamknięta' celowo zgodny 1:1 z planem (§6.2) — łącznie ze znakiem diakrytycznym.
export const PAYROLL_PERIOD_STATUSES = [
  'przygotowanie',
  'kalkulacja',
  'weryfikacja',
  'zatwierdzona',
  'zamknięta',
] as const
export type PayrollPeriodStatus = (typeof PAYROLL_PERIOD_STATUSES)[number]

/**
 * Listy, których kwoty są faktem: zatwierdzone i zamknięte. Lista w weryfikacji
 * może się jeszcze przeliczyć, więc oświadczenie dla komornika i wzmianka
 * w świadectwie pracy biorą kwoty tylko z tych stanów (#12, #71, ADR-0036).
 */
export const SETTLED_PAYROLL_PERIOD_STATUSES: readonly PayrollPeriodStatus[] = [
  'zatwierdzona',
  'zamknięta',
]

/**
 * Stan dokumentu w miesiącu. `na` = nie dotyczy tego kompletu: komplet
 * korygujący nie zawiera raportu imiennego, gdy zmiana dotyczy wyłącznie
 * danych deklaracji (poradnik ZUS „Ogólne zasady wypełniania i korygowania
 * dokumentów ubezpieczeniowych", rozdział III).
 */
export const DECLARATION_FILE_STATUSES = ['todo', 'gen', 'sent', 'err', 'na'] as const
export type DeclarationFileStatus = (typeof DECLARATION_FILE_STATUSES)[number]

/** Badania/BHP — status wyliczany z dat (ok = ważne, warn = wygasa ≤30 dni, err = brak/po terminie). */
export const HEALTH_RECORD_STATUSES = ['ok', 'warn', 'err'] as const
export type HealthRecordStatus = (typeof HEALTH_RECORD_STATUSES)[number]

export const CONTRACT_TYPES = [
  'uop_nieokreslony',
  'uop_okreslony',
  'uop_probny',
  'zlecenie',
  /**
   * Umowa o dzieło (art. 627 k.c.). Nie jest tytułem do ubezpieczeń
   * społecznych ani zdrowotnego, więc silnik nie liczy z niej żadnej składki;
   * podatkowo to działalność wykonywana osobiście z art. 13 pkt 8 PIT, z 20 %
   * kosztami z art. 22 ust. 9 pkt 4 albo 50 % z pkt 3 przy przeniesieniu praw
   * autorskich. Ulga dla młodych i ulgi z pkt 152–154 jej NIE obejmują
   * (Etap 46, ADR-0047).
   */
  'dzielo',
  'b2b',
] as const
export type ContractType = (typeof CONTRACT_TYPES)[number]

/**
 * Umowy będące stosunkiem pracy (art. 22 § 1 k.p.). Jedna definicja dla silnika
 * płac, ekranów i dokumentów — dzieło, zlecenie i B2B nimi nie są (ADR-0047).
 */
export const EMPLOYMENT_CONTRACT_TYPES: readonly ContractType[] = [
  'uop_nieokreslony',
  'uop_okreslony',
  'uop_probny',
]

export function isEmploymentContract(type: ContractType): boolean {
  return EMPLOYMENT_CONTRACT_TYPES.includes(type)
}

/**
 * Jak ewidencja czasu dotyczy osoby (fala 7b, #35; D102):
 *  - `kodeks_pracy` — pracownik: wymiar, nadgodziny, pora nocna, rozkład,
 *  - `godziny_zlecenia` — zleceniobiorca: same godziny wykonania, bo z nich
 *    liczy się minimalna stawka godzinowa (art. 8a ustawy o minimalnym
 *    wynagrodzeniu), bez normy i bez nadgodzin,
 *  - `nie_dotyczy` — dzieło i B2B: umowa o rezultat albo usługę firmy, bez
 *    ewidencji godzin.
 */
export const TIME_REGIMES = ['kodeks_pracy', 'godziny_zlecenia', 'nie_dotyczy'] as const
export type TimeRegime = (typeof TIME_REGIMES)[number]

export function timeRegimeOf(type: ContractType): TimeRegime {
  if (isEmploymentContract(type)) return 'kodeks_pracy'
  return type === 'zlecenie' ? 'godziny_zlecenia' : 'nie_dotyczy'
}

/** Rodzaje wpisu, które zleceniobiorca może dodać o sobie — tylko praca. */
export const MANDATE_TIME_ENTRY_KINDS = ['praca'] as const

export const INVITATION_STATUSES = ['pending', 'used', 'expired', 'revoked'] as const
export type InvitationStatus = (typeof INVITATION_STATUSES)[number]

export const AUTH_TOKEN_KINDS = [
  'magic_link',
  'password_reset',
  'password_set',
  'email_verify',
  'onboarding_resume',
] as const
export type AuthTokenKind = (typeof AUTH_TOKEN_KINDS)[number]

export const TENANT_PLANS = ['start', 'pro', 'ent'] as const
export type TenantPlan = (typeof TENANT_PLANS)[number]

export const DOMAIN_VERIFICATION_STATUSES = ['unverified', 'verified'] as const
export type DomainVerificationStatus = (typeof DOMAIN_VERIFICATION_STATUSES)[number]

export const LEAVE_REQUEST_STATUSES = [
  'pending',
  'approved',
  'rejected',
  'change_requested',
] as const
export type LeaveRequestStatus = (typeof LEAVE_REQUEST_STATUSES)[number]

// ---------------------------------------------------------------------------
// Enumy pomocnicze z §6.3 (schemat DB) — oczywiste na tym etapie
// ---------------------------------------------------------------------------

export const JOIN_REQUEST_STATUSES = ['pending', 'approved', 'rejected'] as const
export type JoinRequestStatus = (typeof JOIN_REQUEST_STATUSES)[number]

export const EMPLOYMENT_TERMS_STATUSES = ['draft', 'proposed', 'accepted', 'rejected'] as const
export type EmploymentTermsStatus = (typeof EMPLOYMENT_TERMS_STATUSES)[number]

/**
 * Rodzaje wpisów w ewidencji czasu pracy. `nieusprawiedliwiona` zapisują kadry:
 * rozporządzenie o dokumentacji pracowniczej wymaga w ewidencji WYMIARU
 * nieobecności nieusprawiedliwionych (§ 6 pkt 1 lit. a), a tego nie da się
 * wyprowadzić z braku wpisu — brak wpisu znaczy „nie wypełniono" (Etap 39).
 */
export const TIME_ENTRY_KINDS = [
  'praca',
  'nadgodziny',
  'noc',
  'zdalna',
  'nieusprawiedliwiona',
] as const
export type TimeEntryKind = (typeof TIME_ENTRY_KINDS)[number]

/**
 * Rodzaje wpisu, które pracownik dodaje o sobie w portalu (fala 7b, #39).
 * Nieobecność nieusprawiedliwiona to ustalenie pracodawcy (ADR-0040 §2),
 * więc formularz jej nie proponuje, a serwer odrzuca ją z kodem `kind`.
 */
export const EMPLOYEE_TIME_ENTRY_KINDS = ['praca', 'nadgodziny', 'noc', 'zdalna'] as const
export type EmployeeTimeEntryKind = (typeof EMPLOYEE_TIME_ENTRY_KINDS)[number]

export const MEDICAL_EXAMINATION_STATUSES = [
  'referred',
  'in_progress',
  'confirmed',
  'approved',
  /**
   * Skierowanie zastąpione nowszym albo wycofane (fala 8, grupa 28). Zostaje
   * w bazie jako ślad, ale nie ma stanu — na osobę jest jedno skierowanie
   * w toku (migracja 0063).
   */
  'cancelled',
] as const
export type MedicalExaminationStatus = (typeof MEDICAL_EXAMINATION_STATUSES)[number]

export const BHP_TRAINING_KINDS = ['wstepne', 'okresowe', 'ppoz', 'stanowiskowe'] as const
export type BhpTrainingKind = (typeof BHP_TRAINING_KINDS)[number]

/** Statusy szkolenia BHP — kolumna tekstowa, więc lista pilnuje wartości w kodzie. */
export const BHP_TRAINING_STATUSES = ['planned', 'completed', 'cancelled'] as const
export type BhpTrainingStatus = (typeof BHP_TRAINING_STATUSES)[number]

/**
 * Rodzaj badania, na które kieruje pracodawca (art. 229 § 1 i § 2 k.p.):
 * wstępne przed dopuszczeniem do pracy, okresowe w trakcie zatrudnienia,
 * kontrolne po niezdolności dłuższej niż 30 dni (fala 8, grupa 28).
 */
export const MEDICAL_REFERRAL_KINDS = ['wstepne', 'okresowe', 'kontrolne'] as const
export type MedicalReferralKind = (typeof MEDICAL_REFERRAL_KINDS)[number]

export const ACKNOWLEDGEMENT_KINDS = [
  'rodo',
  'info_dodatkowa',
  'regulamin',
  'terms',
  'other',
] as const
export type AcknowledgementKind = (typeof ACKNOWLEDGEMENT_KINDS)[number]

export const ONBOARDING_MESSAGE_AUTHORS = ['employee', 'hr'] as const
export type OnboardingMessageAuthor = (typeof ONBOARDING_MESSAGE_AUTHORS)[number]

export const DOCUMENT_TEMPLATE_STATUSES = ['draft', 'published', 'archived'] as const
export type DocumentTemplateStatus = (typeof DOCUMENT_TEMPLATE_STATUSES)[number]

/** Pozycja rozwoju: wniosek → zatwierdzona → w trakcie → ukończona (spec 01 §16). */
export const DEV_ITEM_STATUSES = ['wniosek', 'zatwierdzone', 'w_trakcie', 'ukonczone'] as const
export type DevItemStatus = (typeof DEV_ITEM_STATUSES)[number]

/**
 * Przejścia pozycji rozwoju (fala 8, grupa 58; D105): wniosek → zatwierdzone →
 * w trakcie → ukończone, z możliwością oznaczenia zatwierdzonej pozycji od razu
 * jako ukończonej. Cofnięcia nie ma — zatwierdzenie obciąża budżet roku, a jego
 * odwołanie to inna decyzja niż „powrót do wniosku".
 */
export const DEV_ITEM_TRANSITIONS: Record<DevItemStatus, readonly DevItemStatus[]> = {
  wniosek: ['zatwierdzone'],
  zatwierdzone: ['w_trakcie', 'ukonczone'],
  w_trakcie: ['ukonczone'],
  ukonczone: [],
}

export function canTransitionDevItem(from: DevItemStatus, to: DevItemStatus): boolean {
  return DEV_ITEM_TRANSITIONS[from].includes(to)
}

export const WORKFLOW_DEF_STATUSES = ['active', 'draft'] as const
export type WorkflowDefStatus = (typeof WORKFLOW_DEF_STATUSES)[number]

/** Wynik zdarzenia audytu (§10 / spec 01: ok → „Sukces", deny → „Odrzucono"). */
export const AUDIT_RESULTS = ['ok', 'deny'] as const
export type AuditResult = (typeof AUDIT_RESULTS)[number]

export const USER_LOCALES = ['pl', 'en'] as const
export type UserLocale = (typeof USER_LOCALES)[number]
